import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { showToastError } from './components/ToastNotifications';
import { getAdminUsersReport } from './hooks/useUsers';
import type { SessionUser } from './types';
import downloadCsv from './utils/downloadCsv';
import { getErrorMessage } from './utils/requestErrors';
import { toTestIdPart } from './utils/testIds';

type AdminUsersReportRow = {
  license?: string;
  marketName?: string;
  organizationNames?: string[];
  adminUsers?: string[];
};

type AdminUsersReportPageProps = {
  currentUser?: SessionUser | null;
};

const toList = (value?: string[]) => (Array.isArray(value) ? value.filter(Boolean) : []);
const joinList = (value?: string[]) => toList(value).join('; ');
const joinCsvLines = (value?: string[]) => toList(value).join('\n');
const getRowTestId = (row: AdminUsersReportRow) => (
  `admin-users-report-row-${toTestIdPart(row.license, 'unknown-license')}-${toTestIdPart(row.marketName, 'unknown-market')}`
);

export default function AdminUsersReportPage({ currentUser }: AdminUsersReportPageProps) {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const hasAdminReports =
    currentUser?.isSuperUser === true ||
    currentUser?.internalRoleActions?.includes('ReadInternalReports') === true;

  const [rows, setRows] = useState<AdminUsersReportRow[]>([]);
  const [filter, setFilter] = useState('');
  const [isLoading, setIsLoading] = useState(hasAdminReports);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (!hasAdminReports) {
      setRows([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getAdminUsersReport()
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        setRows(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        showToastError(getErrorMessage(error, 'Could not retrieve admin users report'));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, [hasAdminReports]);

  const filteredRows = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) => (
      String(row.license || '').toLowerCase().includes(term) ||
      String(row.marketName || '').toLowerCase().includes(term) ||
      toList(row.organizationNames).some((name) => name.toLowerCase().includes(term)) ||
      toList(row.adminUsers).some((user) => user.toLowerCase().includes(term))
    ));
  }, [filter, rows]);

  function handleDownloadCsv() {
    const headers = ['License', 'Market Name', 'Organization Names', 'Admin Users'];
    const csvRows = filteredRows.map((row) => ({
      License: row.license ?? '',
      'Market Name': row.marketName ?? '',
      'Organization Names': joinList(row.organizationNames),
      'Admin Users': joinCsvLines(row.adminUsers),
    }));
    downloadCsv(csvRows, 'admin-users-report.csv', headers);
  }

  if (!hasAdminReports) {
    return (
      <div className="stack" data-testid="page-admin-users-report-no-permission">
        <PageHeader
          title="Admin Users Report"
          actions={
            <button
              className="button"
              type="button"
              onClick={() => navigate('/internal/reports')}
              data-testid="admin-users-report-back"
              style={{ alignSelf: 'flex-start' }}
            >
              Back
            </button>
          }
        />

        <div className="card" data-testid="admin-users-report-forbidden-message" style={{ padding: '2rem' }}>
          <p className="muted">You do not have permission to view this report.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stack" data-testid="page-admin-users-report">
      <PageHeader
        title="Admin Users Report"
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignSelf: 'flex-start' }}>
            <button
              className="button"
              type="button"
              onClick={() => navigate('/internal/reports')}
              data-testid="admin-users-report-back"
            >
              Back
            </button>
            <button
              className="button"
              type="button"
              onClick={handleDownloadCsv}
              disabled={filteredRows.length === 0}
              data-testid="admin-users-report-download-csv"
            >
              Download CSV
            </button>
          </div>
        }
      />

      <div className="card filter-card">
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              aria-label="Filter by license, market, organization, or admin user"
              placeholder="Filter by license, market, organization, or admin user"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              data-testid="admin-users-report-filter"
            />
          </div>
        </div>
      </div>

      <div data-testid="admin-users-report-table">
      <table className="table" data-testid="admin-users-report-table-content">
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '50%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>License</th>
            <th>Market Name</th>
            <th>Organization Names</th>
            <th>
              <div>Admin Users</div>
              <div className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
                Org - User Name, User Email
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.length === 0 ? (
            <tr data-testid={isLoading ? 'admin-users-report-loading' : filter.trim() ? 'admin-users-report-empty-filter' : 'admin-users-report-empty'}>
              <td colSpan={4} className="muted" style={{ textAlign: 'center' }}>
                {isLoading
                  ? 'Loading...'
                  : filter.trim()
                    ? 'No matching license admin users'
                    : 'No admin users to display'}
              </td>
            </tr>
          ) : (
            filteredRows.map((row, index) => (
              <tr
                key={`${row.license || ''}-${row.marketName || ''}-${index}`}
                data-testid={getRowTestId(row)}
              >
                <td>{row.license}</td>
                <td>{row.marketName}</td>
                <td>{joinList(row.organizationNames)}</td>
                <td>
                  {toList(row.adminUsers).map((adminUser) => (
                    <div key={adminUser}>{adminUser}</div>
                  ))}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
